// kware start edit - Offline Operations: Preview table
// REASON: Displays parsed operations before execution, allowing staff to review
// and confirm before submitting to FOLIO APIs.
// useMemo is used to prevent MCLRenderer infinite re-render caused by
// new object references being created on every render.

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { MultiColumnList, Icon } from '@folio/stripes/components';
import { OPERATION_TYPES } from './ExcelParser';

const TYPE_LABELS = {
  [OPERATION_TYPES.CHECK_OUT]: 'ui-checkout.offline.type.checkOut',
  [OPERATION_TYPES.CHECK_IN]: 'ui-checkout.offline.type.checkIn',
  [OPERATION_TYPES.RENEW]: 'ui-checkout.offline.type.renew',
};

const TYPE_STYLES = {
  [OPERATION_TYPES.CHECK_OUT]: { background: '#1a5276', color: '#ffffff' },
  [OPERATION_TYPES.CHECK_IN]: { background: '#1e8449', color: '#ffffff' },
  [OPERATION_TYPES.RENEW]: { background: '#b7950b', color: '#ffffff' },
};

const VISIBLE_COLUMNS = ['displayId', 'type', 'patronBarcode', 'itemBarcode', 'actionDate', 'offlineComment'];

const COLUMN_WIDTHS = {
  displayId: '50px',
  type: '120px',
  patronBarcode: '160px',
  itemBarcode: '160px',
  actionDate: '170px',
};

const OperationsPreviewTable = ({ operations, offlineComment }) => {
  const isCheckIn = operations.length > 0 && operations[0].type === OPERATION_TYPES.CHECK_IN;

  const visibleColumns = isCheckIn
    ? ['displayId', 'type', 'itemBarcode', 'actionDate', 'offlineComment']
    : VISIBLE_COLUMNS;
  const numberedOperations = useMemo(
    () => operations.map((op, idx) => ({
      ...op,
      displayId: idx + 1,
      offlineComment: offlineComment(op),
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [operations.length, offlineComment]
  );

  const columnMapping = useMemo(() => ({
    displayId: '#',
    type: <FormattedMessage id="ui-checkout.offline.column.type" />,
    patronBarcode: <FormattedMessage id="ui-checkout.offline.column.patronBarcode" />,
    itemBarcode: <FormattedMessage id="ui-checkout.offline.column.itemBarcode" />,
    actionDate: <FormattedMessage id="ui-checkout.offline.column.actionDate" />,
    offlineComment: <FormattedMessage id="ui-checkout.offline.column.offlineComment" />,
  }), []);

  const formatter = useMemo(() => ({
    displayId: (row) => row.displayId,
    type: (row) => (
      <span style={{
        ...TYPE_STYLES[row.type],
        padding: '3px 10px',
        borderRadius: '10px',
        fontSize: '12px',
        fontWeight: 'bold',
        whiteSpace: 'nowrap',
      }}
      >
        <FormattedMessage id={TYPE_LABELS[row.type]} />
      </span>
    ),
    patronBarcode: (row) => row.patronBarcode || <Icon icon="minus" />,
    itemBarcode: (row) => row.itemBarcode,
    actionDate: (row) => (row.actionDate ? row.actionDate.replace('T', ' ').replace('.000Z', '') : ''),
    offlineComment: (row) => (
      <span style={{ fontSize: '11px', color: '#555', fontStyle: 'italic' }}>
        {row.offlineComment}
      </span>
    ),
  }), []);

  return (
    <MultiColumnList
      contentData={numberedOperations}
      formatter={formatter}
      visibleColumns={visibleColumns}
      columnMapping={columnMapping}
      columnWidths={COLUMN_WIDTHS}
      isEmptyMessage={<FormattedMessage id="ui-checkout.offline.noOperations" />}
    />
  );
};

OperationsPreviewTable.propTypes = {
  operations: PropTypes.arrayOf(PropTypes.object).isRequired,
  offlineComment: PropTypes.func.isRequired,
};

export default OperationsPreviewTable;
// kware end edit
